import { SEMANTIC_SUCCESS } from '@virtuetasks/types';
/**
 * Project dashboard — spec §5-F13, §8.1 `/projects/:key/dashboard`.
 *
 * Status distribution, burn-up, cycle time, overdue and blocked lists, top
 * contributors, estimate accuracy, open-vs-closed, and the time-tracking
 * compliance widget when the project requires time (F9).
 */

import { Link, useParams } from 'react-router';
import { AlertTriangle, Ban } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { useProject } from '@/features/projects';
import { BurnUpChart, ChartCard, SimpleLineChart, StatCard, StatusDonut } from './charts';
import { useProjectDashboard } from './api';

export function ProjectDashboardPage() {
  const { key } = useParams();
  const { data: project } = useProject(key);
  const { data, isLoading } = useProjectDashboard(project?._id);

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Dashboard" subtitle={project?.name} />
        <div className="p-4">
          <Skeleton className="h-64" />
        </div>
      </>
    );
  }

  const accuracy = data.estimateAccuracy;
  const accuracyPct = accuracy.estimated ? Math.round(accuracy.ratio * 100) : 0;

  return (
    <>
      <PageHeader title="Dashboard" subtitle={project ? `${project.name} — health at a glance` : undefined} />

      <div className="flex flex-col gap-3 p-4" data-testid="project-dashboard">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Cycle time (median)"
            value={`${data.cycleTimeDays.median}d`}
            hint={`p85 ${data.cycleTimeDays.p85}d · n=${data.cycleTimeDays.sample}`}
            testId="stat-project-cycle"
          />
          <StatCard
            label="Overdue"
            value={data.overdue.length}
            tone={data.overdue.length ? 'danger' : 'default'}
          />
          <StatCard
            label="Blocked"
            value={data.blocked.length}
            tone={data.blocked.length ? 'warn' : 'default'}
          />
          <StatCard
            label="Estimate accuracy"
            value={accuracy.estimated ? `${accuracyPct}%` : '—'}
            hint={
              accuracy.estimated
                ? `${Math.round(accuracy.spent / 60)}h spent vs ${Math.round(accuracy.estimated / 60)}h estimated`
                : 'no estimates yet'
            }
          />
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <ChartCard title="Status distribution" testId="chart-status">
            {data.statusDistribution.length === 0 ? (
              <p className="py-8 text-center text-[12px] vt-muted">No tasks yet.</p>
            ) : (
              <StatusDonut data={data.statusDistribution} />
            )}
          </ChartCard>

          <ChartCard title="Burn-up" subtitle="Scope vs completed" testId="chart-burnup">
            {data.burnUp.length === 0 ? (
              <p className="py-8 text-center text-[12px] vt-muted">Not enough history yet.</p>
            ) : (
              <BurnUpChart data={data.burnUp} />
            )}
          </ChartCard>

          <ChartCard title="Open vs closed" subtitle="Last 12 weeks" testId="chart-open-closed">
            {data.openVsClosed.length === 0 ? (
              <p className="py-8 text-center text-[12px] vt-muted">Not enough history yet.</p>
            ) : (
              <SimpleLineChart
                data={data.openVsClosed.map((r) => ({ ...r, week: r.week.slice(5) })) as never}
                xKey="week"
                series={[
                  { key: 'opened', label: 'Opened', colour: '#4A7CB5' },
                  { key: 'closed', label: 'Closed', colour: SEMANTIC_SUCCESS },
                ]}
              />
            )}
          </ChartCard>

          <ChartCard title="Top contributors" testId="chart-contributors">
            {data.topContributors.length === 0 ? (
              <p className="py-8 text-center text-[12px] vt-muted">Nothing completed yet.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {data.topContributors.map((person) => (
                  <li key={person.userId} className="flex items-center gap-2 text-[12px]">
                    <span className="flex-1 truncate">{person.name}</span>
                    <span className="vt-mono font-medium">{person.completed}</span>
                  </li>
                ))}
              </ul>
            )}
          </ChartCard>
        </div>

        {data.timeCompliance && (
          <div className="vt-surface p-3" data-testid="time-compliance">
            <h3 className="text-[13px] font-medium">Time-tracking compliance</h3>
            <p className="mt-1 text-[12px] vt-muted">
              {data.timeCompliance.withoutTime} of {data.timeCompliance.completed} completed tasks
              have no time logged ({100 - data.timeCompliance.percentage}% compliant). A nudge, never
              a block — hard blocks breed fake entries.
            </p>
          </div>
        )}

        <div className="grid gap-3 lg:grid-cols-2">
          <section className="vt-surface overflow-hidden">
            <h3 className="border-b border-[var(--border)] px-3 py-2 text-[13px] font-medium">
              Overdue
            </h3>
            {data.overdue.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon={<AlertTriangle className="size-5" />}
                  title="Nothing overdue"
                  description="Keep it that way."
                />
              </div>
            ) : (
              data.overdue.map((task) => (
                <Link
                  key={task.key}
                  to={`/tasks/${task.key}`}
                  className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2 text-[13px] last:border-0 hover:bg-[var(--surface-2)]"
                >
                  <span className="vt-mono text-[11px] vt-muted">{task.key}</span>
                  <span className="min-w-0 flex-1 truncate">{task.title}</span>
                  <Badge tone="danger">{task.daysOverdue}d</Badge>
                </Link>
              ))
            )}
          </section>

          <section className="vt-surface overflow-hidden">
            <h3 className="border-b border-[var(--border)] px-3 py-2 text-[13px] font-medium">
              Blocked
            </h3>
            {data.blocked.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon={<Ban className="size-5" />}
                  title="Nothing blocked"
                  description="No one is waiting on anyone."
                />
              </div>
            ) : (
              data.blocked.map((task) => (
                <Link
                  key={task.key}
                  to={`/tasks/${task.key}`}
                  className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2 text-[13px] last:border-0 hover:bg-[var(--surface-2)]"
                >
                  <span className="vt-mono text-[11px] vt-muted">{task.key}</span>
                  <span className="min-w-0 flex-1 truncate">
                    {task.title}
                    {task.reason ? <span className="vt-muted"> — {task.reason}</span> : null}
                  </span>
                  <Badge tone="warn">{task.days}d</Badge>
                </Link>
              ))
            )}
          </section>
        </div>
      </div>
    </>
  );
}
